import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listSessions, revokeOtherSessions, revokeSession, securityLog,
} from '../../api/account.ts';
import { changePassword } from '../../api/auth.ts';
import { formatDateTime, formatRelative } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';
import { ConfirmDialog } from '../common/ConfirmDialog.tsx';
import { IconDevice, IconLock } from '../../lib/icons.tsx';

const ACTION_LABEL: Record<string, string> = {
  'auth.login': '로그인',
  'auth.login_failed': '로그인 실패',
  'auth.password_changed': '비밀번호 변경',
  'auth.pairing': 'PC 연결',
  'device.revoked': 'PC 연결 해제',
  'share.added': '공유 폴더 참여',
};

export function SecurityTab() {
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  const sessions = useQuery({ queryKey: ['sessions'], queryFn: listSessions });
  const log = useQuery({ queryKey: ['security-log'], queryFn: securityLog });

  const revoke = useMutation({
    mutationFn: revokeSession,
    onSuccess: () => {
      toast.success('해당 기기에서 로그아웃했습니다');
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (e: Error) => { toast.error(e); },
  });

  const revokeAll = useMutation({
    mutationFn: revokeOtherSessions,
    onSuccess: (r) => {
      toast.success(`${r.revoked}개 세션을 종료했습니다`);
      setConfirmAll(false);
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (e: Error) => { toast.error(e); setConfirmAll(false); },
  });

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success('비밀번호를 변경했습니다. 다른 기기는 다시 로그인해야 합니다.');
      setCurrent(''); setNext('');
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  const items = sessions.data?.items ?? [];
  const others = items.filter((s) => !s.is_current).length;

  return (
    <>
      <section className="panel">
        <h2>비밀번호</h2>
        <p className="panel__hint">
          변경하면 이 브라우저를 제외한 모든 기기가 로그아웃됩니다.
        </p>
        <form onSubmit={(e) => { void submit(e); }}>
          <div className="field">
            <label htmlFor="cur">현재 비밀번호</label>
            <input id="cur" className="input" type="password" required autoComplete="current-password"
              value={current} onChange={(e) => { setCurrent(e.target.value); }} />
          </div>
          <div className="field">
            <label htmlFor="new">새 비밀번호 (10자 이상)</label>
            <input id="new" className="input" type="password" required minLength={10}
              autoComplete="new-password"
              value={next} onChange={(e) => { setNext(e.target.value); }} />
          </div>
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? <span className="spinner" /> : '변경하기'}
          </button>
        </form>
      </section>

      <section className="panel">
        <div className="row" style={{ marginBottom: 4 }}>
          <h2 className="spacer">로그인된 기기</h2>
          {others > 0 && (
            <button className="btn btn--sm" onClick={() => { setConfirmAll(true); }}>
              다른 기기 모두 로그아웃
            </button>
          )}
        </div>
        <p className="panel__hint">
          모르는 기기가 있으면 바로 로그아웃하고 비밀번호를 바꾸세요.
        </p>

        {sessions.isLoading && <span className="spinner" />}
        {items.map((session) => (
          <div key={session.id} className="panel__row">
            {session.kind === 'desktop' ? <IconDevice size={18} /> : <IconLock size={18} />}
            <div className="spacer" style={{ minWidth: 0 }}>
              <div className="row" style={{ gap: 6 }}>
                <strong style={{ fontSize: 13 }}>
                  {session.device_name ?? session.user_agent ?? '알 수 없는 기기'}
                </strong>
                {session.is_current && <span className="frow__badge">현재 기기</span>}
                {session.kind === 'desktop' && <span className="frow__badge">PC 앱</span>}
              </div>
              <div className="faint" style={{ fontSize: 12 }}>
                {session.ip ?? 'IP 없음'}
                {' · 마지막 사용 '}
                {session.last_used_at ? formatRelative(session.last_used_at) : '기록 없음'}
              </div>
            </div>
            {!session.is_current && (
              <button
                className="btn btn--sm"
                onClick={() => { revoke.mutate(session.id); }}
              >
                로그아웃
              </button>
            )}
          </div>
        ))}
      </section>

      <section className="panel">
        <h2>최근 보안 활동</h2>
        <p className="panel__hint">로그인, 기기 연결, 비밀번호 변경 기록입니다.</p>
        {(log.data?.items ?? []).length === 0 && <p className="muted">기록이 없습니다.</p>}
        {(log.data?.items ?? []).map((event, index) => (
          <div key={`${event.created_at}-${index}`} className="panel__row">
            <div className="spacer">
              <div style={{ fontSize: 13 }}>{ACTION_LABEL[event.action] ?? event.action}</div>
              <div className="faint" style={{ fontSize: 12 }}>
                {formatDateTime(event.created_at)}
                {event.ip && ` · ${event.ip}`}
              </div>
            </div>
          </div>
        ))}
      </section>

      {confirmAll && (
        <ConfirmDialog
          title="다른 기기 모두 로그아웃"
          message={`현재 브라우저를 제외한 ${others}개 세션을 종료합니다.\nPC 클라이언트는 다시 연결해야 합니다.`}
          confirmLabel="모두 로그아웃" danger busy={revokeAll.isPending}
          onConfirm={() => { revokeAll.mutate(); }}
          onCancel={() => { setConfirmAll(false); }}
        />
      )}
    </>
  );
}
